// src/data/conge-demenagement.ts
//
// Congé de déménagement et droits du locataire — LOT 12 du plan SEO.
// Page rattachée au hub changement d'adresse (lot 11), même mécanique.
//
// La contrainte de ce lot est juridique, et elle est appliquée ici par la
// FORME des données : une règle qui cite un délai, un montant ou un article
// cite une source officielle (`sourceId`) ; une source est une URL https
// d'un domaine .be ; la page porte un avertissement qui dit qu'elle informe
// et ne conseille pas ; et rien n'est servi tant qu'un humain n'a pas relu
// (`verifiePar`). Le contrôle 12f du build et le test appliquent le même
// validateur.
import donnees from './conge-demenagement.json';
import { HUB_CHANGEMENT_ADRESSE, type SourceOfficielle } from './changement-adresse';

export const PAGE_CONGE_DEMENAGEMENT = `${HUB_CHANGEMENT_ADRESSE}/conge-demenagement`;

export interface RegleJuridique {
  texte: string;
  /** Source officielle, par id dans `SOURCES`. `null` seulement pour un rappel sans fait. */
  sourceId: string | null;
  /** Article du texte cité, quand la source est un texte légal : « art. 55, § 5 ». */
  reference?: string;
}

export interface SectionConge {
  id: string;
  titre: string;
  intro: string;
  regles: RegleJuridique[];
}

export interface QuestionConge {
  q: string;
  a: string;
  sourceId: string | null;
}

export interface PageConge {
  publiable: boolean;
  verifiePar: 'human' | null;
  dateVerification: string | null;
  aValider: string[];
}

interface Donnees {
  page: PageConge;
  sources: SourceOfficielle[];
  avertissement: string;
  sections: SectionConge[];
  faq: QuestionConge[];
}

const D = donnees as unknown as Donnees;

export const PAGE: PageConge = D.page;
export const SOURCES: readonly SourceOfficielle[] = D.sources;
export const AVERTISSEMENT: string = D.avertissement;
export const SECTIONS: readonly SectionConge[] = D.sections;
export const FAQ: readonly QuestionConge[] = D.faq;

export function sourceParId(id: string | null | undefined): SourceOfficielle | undefined {
  return id ? SOURCES.find((s) => s.id === id) : undefined;
}

export function pageVerifiee(): boolean {
  return PAGE.verifiePar === 'human' && Boolean(PAGE.dateVerification);
}

export interface ProblemeConge {
  ou: string;
  gravite: 'erreur' | 'avertissement';
  message: string;
}

const URL_OFFICIELLE = /^https:\/\/[^/]+\.be(\/|$)/;
// Un délai, un montant, un article : ce qu'on n'écrit pas sans source.
const FAIT_CHIFFRE = /\b(un|deux|trois|six|neuf|\d+) (jours?|mois|ans?|années?)\b|\d+ ?€|art\. ?\d+/i;

export function validerCongeDemenagement(aujourdhui: Date = new Date()): ProblemeConge[] {
  const p: ProblemeConge[] = [];
  const limite = new Date(aujourdhui);
  limite.setUTCFullYear(limite.getUTCFullYear() - 1);
  const perimee = (iso: string | null) => Boolean(iso) && new Date(iso as string) < limite;
  const ids = new Set(SOURCES.map((s) => s.id));

  for (const s of SOURCES) {
    if (!URL_OFFICIELLE.test(s.url)) p.push({ ou: `sources.${s.id}`, gravite: 'erreur', message: `url « ${s.url} » n'est pas une URL https d'un domaine .be` });
    if (perimee(s.dateVerification)) p.push({ ou: `sources.${s.id}`, gravite: 'avertissement', message: `source vérifiée il y a plus de 12 mois (${s.dateVerification})` });
  }

  if (!/informe/.test(AVERTISSEMENT) || !/conseille pas/.test(AVERTISSEMENT) || !/(employeur|secrétariat social)/.test(AVERTISSEMENT)) {
    p.push({ ou: 'avertissement', gravite: 'erreur', message: "l'avertissement doit dire que la page informe, ne conseille pas, et renvoyer à l'employeur ou au secrétariat social" });
  }

  for (const s of SECTIONS) {
    if (!s.regles.length) p.push({ ou: `sections.${s.id}`, gravite: 'erreur', message: 'section sans règle' });
    for (const [i, r] of s.regles.entries()) {
      const ou = `sections.${s.id}[${i}]`;
      if (r.sourceId && !ids.has(r.sourceId)) p.push({ ou, gravite: 'erreur', message: `source inconnue : ${r.sourceId}` });
      if (!r.sourceId && FAIT_CHIFFRE.test(r.texte)) p.push({ ou, gravite: 'erreur', message: `règle chiffrée sans source : « ${r.texte.slice(0, 60)}… »` });
      if (r.sourceId === 'decret' && !r.reference) p.push({ ou, gravite: 'erreur', message: 'une règle tirée du décret cite son article (reference)' });
    }
  }

  if (FAQ.length < 4 || FAQ.length > 8) p.push({ ou: 'faq', gravite: 'erreur', message: `${FAQ.length} question(s) — il en faut entre 4 et 8` });
  for (const [i, f] of FAQ.entries()) {
    if (f.sourceId && !ids.has(f.sourceId)) p.push({ ou: `faq[${i}]`, gravite: 'erreur', message: `source inconnue : ${f.sourceId}` });
    if (!f.sourceId && FAIT_CHIFFRE.test(f.a)) p.push({ ou: `faq[${i}]`, gravite: 'erreur', message: `réponse chiffrée sans source : « ${f.q} »` });
  }

  if (PAGE.verifiePar !== 'human' && PAGE.verifiePar !== null) p.push({ ou: 'page', gravite: 'erreur', message: `verifiePar vaut « ${PAGE.verifiePar} » — attendu "human" ou null` });
  if (PAGE.publiable && !pageVerifiee()) p.push({ ou: 'page', gravite: 'erreur', message: 'publiable=true mais page non relue (verifiePar, dateVerification)' });
  if (PAGE.verifiePar === null) p.push({ ou: 'page', gravite: 'avertissement', message: 'page collectée mais non relue (verifiePar: null) — marqueur « À VALIDER » rendu' });
  if (perimee(PAGE.dateVerification)) p.push({ ou: 'page', gravite: 'avertissement', message: `page relue il y a plus de 12 mois (${PAGE.dateVerification})` });
  return p;
}
